import React, { FC, useState } from 'react';
import { Card, Heading, Text, TextButton } from '@wix/design-system';
import { CliTemplate } from './types';
import s from './CliTemplateCard.scss';
import blankCanvasUrl from '../../../assets/svg/templates/blank-canvas.svg?url';
import chartWidgetUrl from '../../../assets/svg/templates/chart-widget.svg?url';
import customProductsCatalogUrl from '../../../assets/svg/templates/custom-products-catalog.svg?url';
import inventoryCountdownUrl from '../../../assets/svg/templates/inventory-countdown.svg?url';
import mixpanelAnalyticsUrl from '../../../assets/svg/templates/mixpanel-analytics.svg?url';
import customShippingRatesUrl from '../../../assets/svg/templates/custom-shipping-rates.svg?url';
import sitePopupUrl from '../../../assets/svg/templates/site-popup.svg?url';

const TEMPLATE_URL_BY_ID: Record<string, string> = {
  '5fcf03b8-46ba-499b-8dbb-40ecf811aceb': chartWidgetUrl,
  '46746ee0-e0b0-4971-be34-f3ea94dd979b': sitePopupUrl,
  '24493a0d-18f2-4f68-b6d5-55992cef7daa': customProductsCatalogUrl,
  'c442b755-2276-4336-918a-915865a9fa2b': mixpanelAnalyticsUrl,
};

// Title-based fallback for templates whose IDs are not yet known from the API
const TEMPLATE_URL_BY_TITLE: Record<string, string> = {
  'Inventory Countdown': inventoryCountdownUrl,
  'Custom Shipping Rates (CLI)': customShippingRatesUrl,
};

interface CliTemplateCardProps {
  template: CliTemplate;
  onClick: (template: CliTemplate) => void;
}

export const CliTemplateCard: FC<CliTemplateCardProps> = ({
  template,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClick(template)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(template);
        }
      }}
      className={s.cardWrapper}
    >
      <Card showShadow={hovered} className={s.gridCard}>
        <div className={s.cardContent}>
          <TemplateImage template={template} />
          <div className={s.textContent}>
            <div className={s.textArea}>
              <Heading ellipsis size="medium" maxLines={2}>
                {template.title?.replace(/\s*\(CLI\)$/i, '')}
              </Heading>
              <Text secondary size="small" maxLines={2}>
                {template.subtitle}
              </Text>
            </div>
            <TextButton
              size="small"
              weight="normal"
              skin="dark"
              underline="always"
              className={s.cta}
            >
              Create App
            </TextButton>
          </div>
        </div>
      </Card>
    </div>
  );
};

interface TemplateImageProps {
  template: CliTemplate;
}

const TemplateImage = ({ template }: TemplateImageProps) => {
  const localUrl =
    template.id === 'start-from-scratch-template'
      ? blankCanvasUrl
      : (template.id && TEMPLATE_URL_BY_ID[template.id]) ||
        (template.title && TEMPLATE_URL_BY_TITLE[template.title]);

  return (
    <div className={s.thumbnailWrapper}>
      <img
        src={localUrl || template.thumbnail}
        alt={template.title}
        className={s.templateItemImage}
      />
    </div>
  );
};
